// src/components/Header/UberHeader.jsx
import React, { useState, useMemo } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  useScrollTrigger,
  useTheme as useMuiTheme,
  alpha,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Package,
  Truck,
  LogOut,
  Settings,
  Sun,
  Moon,
  ChevronDown,
  Info,
  BookOpen,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { useUser } from '../../context/UserContext';
import { Building2 } from 'lucide-react';

// ─── BriefingDot ──────────────────────────────────────────────────────────────

const BriefingDot = () => (
  <Box
    sx={{
      width: 7,
      height: 7,
      borderRadius: '50%',
      bgcolor: 'warning.main',
      flexShrink: 0,
    }}
  />
);

// ─── UberHeader ───────────────────────────────────────────────────────────────

const UberHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useMuiTheme();
  const { user, logout } = useAuth();
  const { userData } = useUser();

  const { t, i18n } = useTranslation();
  const { isDark, toggleTheme } = useTheme();

  const [menuAnchor, setMenuAnchor] = useState(null);


  const LANGUAGES = [
    { code: "fi", flag: "🇫🇮", label: "Suomi",    short: "FI" },
    { code: "en", flag: "🇬🇧", label: "English",  short: "EN" },
    { code: "fr", flag: "🇫🇷", label: "Français", short: "FR" },
  ];
  const currentLang = LANGUAGES.find((l) => l.code === i18n.language) ?? LANGUAGES[0];
  const [langAnchor, setLangAnchor] = useState(null);
  const trigger = useScrollTrigger({ disableHysteresis: true, threshold: 0 });

  const isCourier           = userData?.mode === 'courier' ||
                              userData?.roles?.includes('courier') ||
                              userData?.roles?.includes('specialCourier');
  const needsBriefing       = isCourier && !userData?.briefingRead;

  const isSender            = userData?.mode === 'sender';
  const needsSenderBriefing = isSender && !userData?.senderBriefingRead;

  const hasAnyAlert         = needsBriefing || needsSenderBriefing;

  const handleLogout = async () => {
    setMenuAnchor(null);
    await logout();
    navigate("/");
  };

  const getUserInitials = () => {
    if (!user?.email) return "U";
    return user.email.charAt(0).toUpperCase();
  };

  const menuItems = useMemo(() => {
    const roles  = userData?.roles  || [];
    const mode   = userData?.mode   || 'sender';

    const isCourierMode    = mode === 'courier';
    const isSpecialCourier = roles.includes('specialCourier');

    const laheta    = { label: t('header.sendPackage'),    icon: <Package   size={16} />, path: '/laheta-paketti' };
    const kuljeta   = { label: t('header.carry'),          icon: <Truck     size={16} />, path: '/kuljeta-paketti' };
    const org       = { label: t('header.organisation'),   icon: <Building2 size={16} />, path: '/profile?tab=organisaatio' };
    const payments  = { label: t('header.payments'),       icon: <Building2 size={16} />, path: '/profile?tab=maksut' };
    const asetukset = { label: t('header.settings'),       icon: <Settings  size={16} />, path: '/profile', briefingAlert: true };
    const info      = { label: t('header.info'),           icon: <Info      size={16} />, path: '/info' };

    if (isSpecialCourier) return [laheta, kuljeta, org, asetukset, info];
    if (isCourierMode)    return [kuljeta, asetukset, payments, info];
    return                       [laheta, asetukset, info];
  }, [userData?.roles, userData?.mode, t]);

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        bgcolor: trigger
          ? alpha(muiTheme.palette.background.paper, 0.92)
          : "background.paper",
        backdropFilter: "blur(12px)",
        borderBottom: "1px solid",
        borderColor: "divider",
        transition: "border-color 0.2s ease, background-color 0.2s ease",
      }}
    >
      <Container maxWidth="xl">
        <Toolbar
          disableGutters
          sx={{ px: { xs: 1, sm: 2 }, minHeight: { xs: 64, sm: 72 } }}
        >
          {/* ── Logo ── */}
          <Box
            onClick={() => isSender ? navigate("/") : navigate("/kuljeta-paketti")}
            sx={{ display: "flex", alignItems: "center", gap: 1.25, cursor: "pointer", flexShrink: 0 }}
          >
            <Box
              component="img"
              src="/logo192.png"
              alt="Pakettihuolto"
              sx={{
                width: 34, height: 34, borderRadius: "10px",
                objectFit: "contain", flexShrink: 0,
                border: "1px solid", borderColor: "divider",
              }}
            />
            <Typography
              variant="subtitle1"
              fontWeight={800}
              letterSpacing="-0.5px"
              sx={{ display: { xs: "none", sm: "block" }, color: "text.primary", lineHeight: 1 }}
            >
              Pakettihuolto
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* ── Right actions ── */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>

            {/* Theme toggle */}
            <IconButton
              onClick={toggleTheme}
              size="small"
              sx={{
                color: "text.secondary", width: 36, height: 36,
                borderRadius: "8px", flexShrink: 0,
                "&:hover": { bgcolor: "action.hover", color: "text.primary" },
              }}
            >
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </IconButton>

            {/* ── Language dropdown ── */}
            <Button
              onClick={(e) => setLangAnchor(e.currentTarget)}
              size="small"
              endIcon={
                <ChevronDown
                  size={12}
                  style={{
                    transition: "transform 0.2s",
                    transform: Boolean(langAnchor) ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              }
              sx={{
                minWidth: 76, color: "text.secondary",
                fontWeight: 600, fontSize: "0.8rem", px: 1, py: 0.5,
                borderRadius: "8px", textTransform: "none", lineHeight: 1,
                flexShrink: 0,
                "&:hover": { bgcolor: "action.hover", color: "text.primary" },
              }}
            >
              {currentLang.flag} {currentLang.short}
            </Button>

            <Menu
              anchorEl={langAnchor}
              open={Boolean(langAnchor)}
              onClose={() => setLangAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              transformOrigin={{ vertical: "top", horizontal: "center" }}
              PaperProps={{
                elevation: 0,
                sx: {
                  mt: 0.5, minWidth: 130, borderRadius: "10px",
                  border: "1px solid", borderColor: "divider",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                  overflow: "hidden",
                },
              }}
            >
              {LANGUAGES.map(({ code, flag, label }) => (
                <MenuItem
                  key={code}
                  selected={i18n.language === code}
                  onClick={() => { i18n.changeLanguage(code); setLangAnchor(null); }}
                  sx={{
                    py: 1, px: 2, gap: 1,
                    fontSize: "0.85rem",
                    fontWeight: i18n.language === code ? 700 : 400,
                    "&.Mui-selected": { bgcolor: "action.selected" },
                    "&.Mui-selected:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <span>{flag}</span>
                  <Typography variant="body2" fontWeight="inherit">{label}</Typography>
                </MenuItem>
              ))}
            </Menu>

            {!user && (
              <Button
                onClick={() => navigate("/auth")}
                variant="contained"
                size="small"
                sx={{
                  ml: 0.5, borderRadius: "10px", textTransform: "none",
                  fontWeight: 700, fontSize: "0.85rem", px: 2, py: 0.75,
                  flexShrink: 0, boxShadow: "none",
                  "&:hover": { boxShadow: "none" },
                }}
              >
                {t("header.login", "Kirjaudu")}
              </Button>
            )}

            {/* ── User menu ── */}
            {user && (
              <>
                <Box
                  onClick={(e) => setMenuAnchor(e.currentTarget)}
                  sx={{
                    position: 'relative',
                    display: "flex", alignItems: "center", gap: 0.5,
                    ml: 0.5, pl: 1, pr: 0.75, py: 0.5,
                    borderRadius: "10px", cursor: "pointer",
                    border: "1px solid", borderColor: "divider",
                    flexShrink: 0, transition: "background-color 0.15s",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 28, height: 28,
                      bgcolor: "primary.main",
                      fontSize: "0.75rem", fontWeight: 700,
                    }}
                  >
                    {getUserInitials()}
                  </Avatar>

                  <ChevronDown
                    size={14}
                    style={{
                      color: muiTheme.palette.text.secondary,
                      transition: "transform 0.2s",
                      transform: Boolean(menuAnchor) ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                  />

                  {/* Notification dot on the avatar trigger */}
                  {hasAnyAlert && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 4, right: 4,
                        width: 8, height: 8,
                        borderRadius: '50%',
                        bgcolor: 'warning.main',
                        border: '1.5px solid',
                        borderColor: 'background.paper',
                        pointerEvents: 'none',
                      }}
                    />
                  )}
                </Box>

                <Menu
                  anchorEl={menuAnchor}
                  open={Boolean(menuAnchor)}
                  onClose={() => setMenuAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  PaperProps={{
                    elevation: 0,
                    sx: {
                      mt: 1, minWidth: 200, borderRadius: "12px",
                      border: "1px solid", borderColor: "divider",
                      boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                      overflow: "hidden",
                    },
                  }}
                >
                  {/* User identity */}
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                      <Avatar
                        sx={{ width: 32, height: 32, bgcolor: "primary.main", fontSize: "0.8rem", fontWeight: 700 }}
                      >
                        {getUserInitials()}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 140 }}>
                          {user?.email}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Divider />

                  {/* Nav items */}
                  {menuItems.map((item) => {
                    const showAlert = item.briefingAlert && hasAnyAlert;
                    const isActive  = location.pathname === item.path?.split('?')[0] &&
                                      !item.path?.includes('?');

                    return (
                      <MenuItem
                        key={item.path}
                        onClick={() => { setMenuAnchor(null); navigate(item.path); }}
                        sx={{
                          py: 1.25, px: 2, gap: 1.5,
                          bgcolor: showAlert
                            ? alpha(muiTheme.palette.warning.main, 0.08)
                            : isActive
                              ? "action.selected"
                              : "transparent",
                          '&:hover': {
                            bgcolor: showAlert
                              ? alpha(muiTheme.palette.warning.main, 0.14)
                              : "action.hover",
                          },
                        }}
                      >
                        {/* Icon */}
                        <Box sx={{ color: showAlert ? 'warning.dark' : 'text.secondary', display: "flex" }}>
                          {showAlert ? <BookOpen size={16} /> : item.icon}
                        </Box>

                        {/* Label */}
                        <Typography
                          variant="body2"
                          fontWeight={showAlert ? 700 : 400}
                          color={showAlert ? 'warning.dark' : 'text.primary'}
                          sx={{ flex: 1 }}
                        >
                          {showAlert ? 'Lue tämä ensin' : item.label}
                        </Typography>

                        {/* Trailing dot */}
                        {showAlert && <BriefingDot />}
                      </MenuItem>
                    );
                  })}

                  <Divider />

                  <MenuItem
                    onClick={handleLogout}
                    sx={{
                      py: 1.25, px: 2, gap: 1.5,
                      "&:hover": { bgcolor: alpha(muiTheme.palette.error.main, 0.06) },
                    }}
                  >
                    <LogOut size={16} color={muiTheme.palette.error.main} />
                    <Typography variant="body2" color="error">
                      {t("header.logout", "Kirjaudu ulos")}
                    </Typography>
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default UberHeader;